import React, { useCallback, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import { InsertEmoticon, Mic, Send, AttachFile, OpenInFull } from "@mui/icons-material";
import FileDropHandler from "../../components/MessageHistory/FileDropHandler";
import FilePreview from "../../components/MessageHistory/FilePreview";
import AudioPreview from "../../components/MessageHistory/AudioPreview";
import RecordingIndicator from "../../components/MessageHistory/RecordingIndicator";
import EmojiPicker from "../../components/MessageHistory/EmojiPicker";
import InternalNoteExpandedEditor from "../../components/MessageHistory/InternalNoteExpandedEditor";
import { composerHasSendPayload } from "../../components/MessageHistory/composeAttachmentHelpers";
import {
  composerHasTextPayload,
  composerPlainText,
  isProbablyHtml,
  stripHtmlToPlain,
} from "../../components/MessageHistory/composerHtmlUtils";
import { COMPOSER_ROW_HEIGHT_PX, composerPrimaryActionButtonSx } from "../../components/MessageHistory/composerConstants";
import { COMPOSER_TEXT_FIELD_SX } from "../../components/MessageHistory/composerTextFieldStyles";
import { useComposerAudioRecording } from "../../components/MessageHistory/useComposerAudioRecording";

const FILE_ACCEPT =
  "image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar";

export default function ClientPortalMessageComposer({ disabled, onSend }) {
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:768px)");
  const fileInputRef = useRef(null);

  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const [expandedEditorOpen, setExpandedEditorOpen] = useState(false);

  const {
    audioURL,
    isRecording,
    recordingDuration,
    startRecording,
    stopRecording,
    discardRecording,
    handleRemoveAudio,
    clearAudio,
  } = useComposerAudioRecording({
    onNotSupported: () => alert("Gravação de áudio não é suportada neste navegador."),
    onPermissionDenied: () => alert("Permita o acesso ao microfone para gravar áudio."),
  });

  const hasText = composerHasTextPayload(message);
  const hasPayload =
    !disabled && (hasText || files.length > 0 || Boolean(audioURL)) && !isRecording;
  const canSend = hasPayload;
  const showMicButton = !hasPayload && !isRecording;
  const showTextComposer = !isRecording && !audioURL;

  const compactMessage = isProbablyHtml(message)
    ? stripHtmlToPlain(message, { trim: false })
    : message;

  const resetComposer = () => {
    setMessage("");
    setFiles([]);
    clearAudio();
  };

  const submitComposer = () => {
    const caption = composerPlainText(message, { trim: true });
    const sendItems = [];

    if (caption) {
      sendItems.push({ message: caption });
    }

    for (const file of files) {
      sendItems.push({ _file: file });
    }

    if (audioURL) {
      sendItems.push({ _audioURL: audioURL });
    }

    if (!sendItems.length) return;

    resetComposer();
    setExpandedEditorOpen(false);

    for (const payload of sendItems) {
      onSend(payload).catch(() => {});
    }
  };

  const handleSend = () => {
    if (!canSend) return;
    submitComposer();
  };

  const handleExpandedSend = () => {
    const canExpandedSend =
      composerHasTextPayload(message) ||
      composerHasSendPayload("", files[0], files, audioURL);
    if (!canExpandedSend || disabled || isRecording) return;
    submitComposer();
  };

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleFileUpload = useCallback(
    (fileOrFiles) => {
      if (!fileOrFiles || isRecording) return;
      const toAdd = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
      setFiles((prev) => [...prev, ...toAdd]);
      clearAudio();
    },
    [isRecording, clearAudio]
  );

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveAudioAndFiles = () => {
    handleRemoveAudio();
    setFiles([]);
  };

  const handleFileUploadInput = (e) => {
    const selected = e.target.files ? Array.from(e.target.files) : [];
    if (selected.length) handleFileUpload(selected);
    e.target.value = "";
  };

  const composerShellBg =
    theme.palette.mode === "dark" ? "background.default" : alpha(theme.palette.grey[50], 0.8);

  const textField = (
    <TextField
      fullWidth
      multiline
      minRows={1}
      maxRows={4}
      variant="outlined"
      placeholder="Digite sua mensagem e pressione enviar…"
      value={compactMessage}
      disabled={disabled}
      onChange={(e) => setMessage(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          if (canSend) handleSend();
        }
      }}
      InputProps={{
        style: {
          borderRadius: 0,
          fontSize: isMobile ? "0.95rem" : "0.9375rem",
        },
      }}
      sx={COMPOSER_TEXT_FIELD_SX}
    />
  );

  const textOrVoiceSlot = isRecording ? (
    <RecordingIndicator
      embedded
      durationSeconds={recordingDuration}
      onStop={stopRecording}
      onDiscard={discardRecording}
    />
  ) : audioURL ? (
    <AudioPreview embedded audioURL={audioURL} onRemove={handleRemoveAudioAndFiles} />
  ) : (
    textField
  );

  const sendActionButton = (
    <Tooltip
      title={
        isRecording
          ? "Pare a gravação para enviar"
          : showMicButton
            ? "Gravar áudio"
            : "Enviar mensagem"
      }
      enterDelay={400}
      disableHoverListener={!isRecording && !showMicButton && !canSend}
    >
      <Box component="span" sx={{ display: "inline-flex", alignSelf: "center", flexShrink: 0 }}>
        <IconButton
          onClick={showMicButton ? startRecording : handleSend}
          disabled={disabled || isRecording || (!showMicButton && !canSend)}
          aria-label={showMicButton ? "Gravar áudio" : "Enviar mensagem"}
          sx={composerPrimaryActionButtonSx()}
        >
          {showMicButton ? <Mic fontSize="small" /> : <Send fontSize="small" />}
        </IconButton>
      </Box>
    </Tooltip>
  );

  return (
    <FileDropHandler onFileUpload={handleFileUpload} dropLabel="Solte arquivos para anexar">
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, width: "100%", minWidth: 0 }}>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              alignItems: "stretch",
              minHeight: COMPOSER_ROW_HEIGHT_PX,
              border: 1,
              borderColor: (th) => alpha(th.palette.divider, th.palette.mode === "dark" ? 0.6 : 1),
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: composerShellBg,
              transition: "border-color 0.15s, box-shadow 0.15s",
              "&:focus-within": {
                borderColor: "primary.main",
                boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
              },
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              hidden
              multiple
              accept={FILE_ACCEPT}
              onChange={handleFileUploadInput}
            />

            {showTextComposer ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  alignSelf: "stretch",
                  px: 0.25,
                  borderRight: 1,
                  borderColor: "divider",
                }}
              >
                <Tooltip title="Anexar arquivo">
                  <span>
                    <IconButton
                      size="small"
                      onClick={openFilePicker}
                      disabled={disabled}
                      aria-label="Anexar arquivo"
                      sx={{ color: "text.secondary", p: 1 }}
                    >
                      <AttachFile sx={{ fontSize: 20 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            ) : null}

            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: `${COMPOSER_ROW_HEIGHT_PX}px`,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {textOrVoiceSlot}
            </Box>

            {showTextComposer ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  alignSelf: "stretch",
                  px: 0.25,
                  borderLeft: 1,
                  borderColor: "divider",
                }}
              >
                <Tooltip title="Ampliar editor">
                  <span>
                    <IconButton
                      size="small"
                      disabled={disabled}
                      onClick={() => setExpandedEditorOpen(true)}
                      aria-label="Ampliar editor"
                      sx={{ color: "text.secondary", p: isMobile ? 0.65 : 1 }}
                    >
                      <OpenInFull sx={{ fontSize: isMobile ? 18 : 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Emoji">
                  <span>
                    <IconButton
                      size="small"
                      disabled={disabled}
                      onClick={(e) => setEmojiAnchorEl(e.currentTarget)}
                      aria-label="Emoji"
                      sx={{ color: "text.secondary", p: 1 }}
                    >
                      <InsertEmoticon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            ) : null}
          </Box>

          {sendActionButton}
        </Box>

        {files.length > 0 ? (
          <Box
            sx={{
              width: "100%",
              mt: 0.75,
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            {files.map((file, index) => (
              <FilePreview
                key={`${file.name}-${file.size}-${index}`}
                file={file}
                onRemove={() => handleRemoveFile(index)}
              />
            ))}
          </Box>
        ) : null}

        <Popover
          open={Boolean(emojiAnchorEl)}
          anchorEl={emojiAnchorEl}
          onClose={() => setEmojiAnchorEl(null)}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          transformOrigin={{ vertical: "bottom", horizontal: "left" }}
          slotProps={{
            paper: { sx: { maxHeight: "min(400px, 70vh)", overflow: "auto" } },
          }}
        >
          <EmojiPicker
            onSelectEmoji={(emoji) => {
              setMessage((prev) => `${typeof prev === "string" ? prev : ""}${emoji}`);
              setEmojiAnchorEl(null);
            }}
          />
        </Popover>

        <InternalNoteExpandedEditor
          open={expandedEditorOpen}
          onClose={() => setExpandedEditorOpen(false)}
          message={message}
          setMessage={setMessage}
          onSend={handleExpandedSend}
          openFilePicker={openFilePicker}
          files={files}
          onRemoveFile={handleRemoveFile}
          audioURL={audioURL}
          title="Escrever mensagem"
          hint="Use o editor ampliado para textos longos, listas e formatação. A mensagem será enviada à equipe de atendimento."
          sendLabel="Enviar mensagem"
          cancelLabel="Cancelar"
          closeAriaLabel="Fechar editor"
        />
      </Box>
    </FileDropHandler>
  );
}
